'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { ISolicitationEntry } from '../../../../contentful/types/ISolicitationEntry';
import { ContentWrap, Css, Responsive, TableRole } from '../../../../ui';
import ErrorMessage from '../../../shared/components/error/ErrorMessage';
import { ErrorMessages } from '../../../../utils/errorMessages';
import { SolicitationListHeader } from './SolicitationListHeader';
import { SolicitationListRow } from './SolicitationListRow';
import { Pagination } from '../../../../components/common/Pagination';
import { useSolicitationsFilter } from '../../hooks/useSolicitationsFilter';
import SolicitationsFilter from './SolicitationsFilter';
import { useScrollIntoView } from '../../../../hooks/useScrollIntoView';
import { useSolicitationsStore } from '../../hooks/useSolicitationsStore';
import { NoResults } from '../../../../components/common/no-results/NoResults';
import NoResultsIllustration from '../../../../assets/no-results-illustration.svg';
import { SolicitationsFilterOptions } from '../../domain/models/SolicitationsFilterOption';

interface ISolicitationsProps {
	solicitations: ISolicitationEntry[];
}

const SOLICITATIONS_PER_PAGE = 12;

export default function SolicitationsList({
	solicitations,
}: ISolicitationsProps) {
	const firstRender = useRef(true);
	const filter = useSolicitationsFilter();
	const { state, submit, getSubmitHref } = filter;
	const { pageIndex, type, category, location, status } = state;
	const { ref, scrollIntoView } = useScrollIntoView(50);

	const { filteredData, setType, setCategory, setLocation, setStatus } =
		useSolicitationsStore(solicitations);

	useEffect(() => {
		// Prevent page scroll on first render
		if (!firstRender.current) {
			scrollIntoView();
		}
		firstRender.current = false;
	}, [pageIndex]);

	useEffect(() => {
		setType(type);
		setCategory(category);
		setLocation(location ? [location] : undefined);
		setStatus(status);
	}, [type, category, location, status]);

	const { pageItems, totalPages } = useMemo(() => {
		const start = pageIndex * SOLICITATIONS_PER_PAGE;
		const end = start + SOLICITATIONS_PER_PAGE;
		return {
			pageItems: filteredData.slice(start, end),
			totalPages: Math.ceil(filteredData.length / SOLICITATIONS_PER_PAGE),
		};
	}, [filteredData, pageIndex]);

	const onSelectFilter = useCallback(
		(filterType: SolicitationsFilterOptions, selectedItem: string) => {
			const newFilterState = {
				...state,
				[filterType]: selectedItem ? [selectedItem] : undefined,
				pageIndex: 0,
			};
			submit(newFilterState);
		},
		[state, submit],
	);

	if (solicitations.length === 0) {
		return <ErrorMessage message={ErrorMessages.SolicitationsNotFound} />;
	}

	if (!filteredData.length) {
		return (
			<StyledContentWrap ref={ref}>
				<SolicitationsFilter
					onSelectFilter={onSelectFilter}
					state={state}
					solicitations={solicitations}
				/>
				<NoResults
					title="No solicitations found"
					message={ErrorMessages.SolicitationsNoResults}
					image={
						<StyledIllustrationWrapper>
							<NoResultsIllustration role="presentation" />
						</StyledIllustrationWrapper>
					}
				/>
			</StyledContentWrap>
		);
	}

	return (
		<StyledContentWrap ref={ref}>
			<SolicitationsFilter
				onSelectFilter={onSelectFilter}
				state={state}
				solicitations={solicitations}
			/>

			<TableRole>
				<SolicitationListHeader />
				{pageItems.map((solicitation, index) => (
					<SolicitationListRow
						key={solicitation.id}
						solicitation={solicitation}
						isFirst={index === 0}
					/>
				))}
			</TableRole>
			<Pagination
				currentPageIndex={pageIndex}
				totalPages={totalPages}
				getHref={(index) => getSubmitHref({ pageIndex: index })}
			/>
		</StyledContentWrap>
	);
}

const StyledContentWrap = styled(ContentWrap)`
	padding: ${Css.pxToRem(30)} 0 ${Css.pxToRem(148)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: 0 0 ${Css.pxToRem(100)};
	}
`;

const StyledIllustrationWrapper = styled.div`
	width: 64px;
	height: 64px;
`;
